import { ReactNode, useEffect, useState } from "react";
import cx from "classnames";
import {
  faAngleRight,
  faArrowsRepeat,
  faCircleQuestion,
  faPlus,
  faQuestionCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { parseNumber } from "common/utils/numbers";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";
import { useSearchState } from "common/router/hooks";
import Icon from "common/components/Icon";
import { pluralized, stringToValidValue } from "common/utils/strings";
import { Optional } from "common/utils/types";

import PillButton from "client/common/components/buttons/PillButton";
import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Subheader from "client/common/components/layouts/Subheader";
import Address from "client/common/components/content/Address";
import PaymentMethod from "client/common/components/content/PaymentMethod";
import LoadingPage from "client/common/components/loading/LoadingPage";
import { ModalStatus } from "client/common/types";
import TextChip from "client/common/components/content/TextChip";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import { useClientQuery, useClientUserQuery } from "client/accounts/hooks/index";
import Protected from "client/common/components/auth/Protected";
import { externalUrls, Paths } from "client/routes";

import { AccountForm } from "../types";
import {
  useAccountUpdateAddressDrawer,
  useAccountUpdateCommunicationPreferencesDrawer,
  useAccountCreatePaymentDrawer,
  useAccountCreatePetDrawer,
  useAccountUpdatePasswordDrawer,
  useAccountUpdatePaymentDrawer,
  useAccountUpdatePetDrawer,
  useAccountUpdatePhoneDrawer,
  useCreateAddressDrawer,
  useAccountControlledSubstancesFieldsUpdateDrawer,
  useControlledSubstancesInfoDrawer,
  useAccountUpdateCommsEmailDrawer,
  useAccountUpdateNameDrawer,
  useAccountUpdateEmailDrawer,
} from "../stackables";
import { useClientPrimaryContactPreferencesQuery } from "../../inbox/hooks";
import Tooltip from "../../common/components/content/Tooltip";
import { useClinic } from "../../clinics/hooks";

import styles from "../styles/AccountPage.module.less";

const noop = () => undefined;

type OptionProps = LinkButtonProps & {
  isPrimary?: boolean;
  autoshipChipContent?: string;
  icon?: IconProp;
};

const Option = ({
  children,
  className = styles.select,
  isPrimary = false,
  autoshipChipContent,
  icon = faAngleRight,
  ...rest
}: OptionProps) => {
  return (
    <LinkButton {...rest} className={cx(styles.option, className)}>
      <div className={styles.optionTop}>
        <span>{children}</span>
        <span className={styles.optionDecoration}>
          {isPrimary && (
            <span>
              <TextChip>Primary</TextChip>
            </span>
          )}
          <Icon fixedWidth={true} icon={icon} className={styles.optionIcon} />
        </span>
      </div>
      {autoshipChipContent && (
        <TextChip>
          <Icon icon={faArrowsRepeat} className={styles.autoshipIcon} />
          <span>{autoshipChipContent}</span>
        </TextChip>
      )}
    </LinkButton>
  );
};

type AddOptionProps = {
  children: ReactNode;
  onClick: () => void;
};

const AddOption = ({ children, ...rest }: AddOptionProps) => {
  return (
    <Option {...rest} className={styles.add} icon={faPlus}>
      <span className={styles.optionTitle}>{children}</span>
    </Option>
  );
};

type AccountDetailFormOpts = {
  form: Optional<AccountForm>;
  isOpen: boolean;
  onClose: () => void;
  id: number;
  isProfileContext: boolean;
};

const useAccountDetailForm = ({
  form,
  isOpen,
  onClose,
  id,
  isProfileContext,
}: AccountDetailFormOpts) => {
  const { open: openAccountPhoneUpdate } = useAccountUpdatePhoneDrawer();
  const { open: openAccountPasswordUpdate } = useAccountUpdatePasswordDrawer();
  const { open: openAccountUpdateName } = useAccountUpdateNameDrawer();
  const { open: openAccountUpdateEmail } = useAccountUpdateEmailDrawer();
  const { open: openAccountUpdateCommsEmail } = useAccountUpdateCommsEmailDrawer();
  const { open: openAccountCommunicationPreferencesUpdate } =
    useAccountUpdateCommunicationPreferencesDrawer();
  const { open: openAccountControlledSubstancesFieldsUpdate } =
    useAccountControlledSubstancesFieldsUpdateDrawer();
  const { open: openAccountAddressUpdate } = useAccountUpdateAddressDrawer();
  const { open: openAccountPaymentUpdate } = useAccountUpdatePaymentDrawer();
  const { open: openAccountPetUpdate } = useAccountUpdatePetDrawer();
  const { open: openCreateAddressDrawer } = useCreateAddressDrawer();
  const { open: openAccountCreatePayment } = useAccountCreatePaymentDrawer();
  const { open: openAccountCreatePet } = useAccountCreatePetDrawer();

  useEffect(() => {
    if (form && isOpen) {
      const formToOpen: Record<AccountForm | "", () => void> = {
        updatePhone: () => openAccountPhoneUpdate({ onClose, isProfileContext }),
        updateCommsPhone: () =>
          openAccountPhoneUpdate({ updateClientPhone: true, onClose, isProfileContext }),
        updateName: () => openAccountUpdateName({ onClose }),
        updateEmail: () => openAccountUpdateEmail({ onClose, isProfileContext }),
        updateCommsEmail: () => openAccountUpdateCommsEmail({ onClose }),
        updatePassword: () => openAccountPasswordUpdate({ onClose }),
        updateCommunicationPreferences: () =>
          openAccountCommunicationPreferencesUpdate({ onClose }),
        updateControlledSubstancesFields: () =>
          openAccountControlledSubstancesFieldsUpdate({ onClose }),
        updateAddress: () => openAccountAddressUpdate({ id, onClose }),
        updatePayment: () => openAccountPaymentUpdate({ id, onClose }),
        updatePet: () => openAccountPetUpdate({ id, onClose }),
        newAddress: () =>
          openCreateAddressDrawer({ displaySubscriptionsBulkUpdate: true, onClose }),
        newPayment: () => openAccountCreatePayment({ onClose }),
        newPet: () => openAccountCreatePet({ onClose }),
        "": noop,
      };
      const open = formToOpen[form ?? ""];
      open();
    }
    // we only want to trigger opening the form when the param changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isOpen]);
};

const ActiveAccountPage = () => {
  const { data: client, isLoading, error } = useClientQuery();
  const {
    data: clientUser,
    isLoading: isClientUserLoading,
    error: clientUserError,
  } = useClientUserQuery();

  const {
    data: primaryContactPreferences,
    isLoading: isPreferencesLoading,
    error: preferencesError,
  } = useClientPrimaryContactPreferencesQuery();

  const [search, setSearch] = useSearchState<Paths["accounts"]["detail"]>({
    form: "0",
    visible: ModalStatus.Closed,
    id: "0",
  });
  const [isSigningOut, setIsSigningOut] = useState(false);
  const clinic = useClinic();
  const useCoOwnersFeatures = clinic.primary_tag.is_pims_backend_enabled;

  useAccountDetailForm({
    form: stringToValidValue(search.form ?? "", Object.values(AccountForm), null),
    isOpen: search.visible === ModalStatus.Open,
    onClose: () => setSearch({ ...search, visible: ModalStatus.Closed }),
    id: parseNumber(search.id, 0),
    isProfileContext: useCoOwnersFeatures,
  });

  const { open: openControlledSubstancesInfoDrawer } = useControlledSubstancesInfoDrawer();

  if (isLoading || isClientUserLoading || isPreferencesLoading) {
    return <LoadingPage />;
  }

  if (
    !client ||
    !clientUser ||
    !primaryContactPreferences ||
    error ||
    clientUserError ||
    preferencesError
  ) {
    return (
      <FetchErrorPage error={error || clientUserError || preferencesError} entityName="account" />
    );
  }

  const facts = [
    {
      label: "Name",
      value: clientUser.full_name,
    },
    {
      label: "Email",
      value: clientUser.primary_contact_email?.email,
    },
    {
      label: "Phone",
      value: clientUser.primary_contact_number?.number_display || "-",
    },
  ];

  const onSignOut = () => {
    setIsSigningOut(true);
    window.location.replace(externalUrls.signout());
  };

  const profileTooltip =
    "The email addresses and phone numbers you use for authentication and logging in.";
  const commMethodsTooltip =
    "The email address and phone numbers that communications from your clinic are sent to.";
  const commMethodsDisabledTooltip =
    "Only primary account owners can update communication preferences.";
  const profileEmailLabel = useCoOwnersFeatures ? "Update profile email" : "Update email";
  const profilePhoneLabel = useCoOwnersFeatures ? "Update profile phone" : "Update phone";
  const commsEmailLabel = "Update communications email";
  const commsPhoneLabel = "Update communications phone";
  const isAccountOwner = client.primary_client_user.id === clientUser.id;

  return (
    <Page>
      <Subheader title="Account" description="Manage your personal information here" />
      <Body>
        <div className={styles.content}>
          <div className={styles.column}>
            <div className={styles.item}>
              <h3 className={styles.heading}>
                <Tooltip
                  className={styles.tooltip}
                  content={profileTooltip}
                  isEnabled={useCoOwnersFeatures}
                >
                  Profile{" "}
                  {useCoOwnersFeatures && (
                    <Icon icon={faQuestionCircle} className={styles.infoIcon} />
                  )}
                </Tooltip>
                <PillButton
                  onClick={onSignOut}
                  variant="secondary"
                  isLoading={isSigningOut}
                  size="s"
                >
                  Signout
                </PillButton>
              </h3>
              <div>
                <div className={styles.facts}>
                  {facts.map((fact) => (
                    <div key={fact.label} className={styles.fact}>
                      <div className={styles.label}>{fact.label}</div>
                      <div className={styles.value}>{fact.value}</div>
                    </div>
                  ))}
                </div>
                <Option
                  onClick={() => setSearch({ form: "updateName", visible: ModalStatus.Open })}
                >
                  <span className={styles.optionTitle}>Update name</span>
                </Option>
                <Option
                  onClick={() => setSearch({ form: "updateEmail", visible: ModalStatus.Open })}
                >
                  <span className={styles.optionTitle}>{profileEmailLabel}</span>
                </Option>
                <Option
                  onClick={() => setSearch({ form: "updatePhone", visible: ModalStatus.Open })}
                >
                  <span className={styles.optionTitle}>{profilePhoneLabel}</span>
                </Option>
                <Option
                  onClick={() => setSearch({ form: "updatePassword", visible: ModalStatus.Open })}
                >
                  <span className={styles.optionTitle}>Update password</span>
                </Option>
                {!useCoOwnersFeatures && (
                  <Option
                    onClick={() =>
                      setSearch({
                        form: "updateCommunicationPreferences",
                        visible: ModalStatus.Open,
                      })
                    }
                  >
                    <span className={styles.optionTitle}>Update communication preferences</span>
                  </Option>
                )}
              </div>
            </div>
            <div className={styles.item}>
              <h3 className={styles.heading}>Pet Information</h3>
              <div className={styles.options}>
                {client.patients.map((patient) => (
                  <Option
                    key={patient.id}
                    onClick={() =>
                      setSearch({
                        form: "updatePet",
                        visible: ModalStatus.Open,
                        id: `${patient.id}`,
                      })
                    }
                  >
                    <div>
                      <div className={styles.optionTitle}>{patient.name_display}</div>
                      <div className={styles.optionDescription}>{patient.description}</div>
                    </div>
                  </Option>
                ))}
                <AddOption onClick={() => setSearch({ form: "newPet", visible: ModalStatus.Open })}>
                  Add pet
                </AddOption>
              </div>
            </div>
          </div>
          <div className={styles.column}>
            {useCoOwnersFeatures && (
              <div className={styles.item}>
                <h3 className={styles.heading}>
                  <Tooltip className={styles.tooltip} content={commMethodsTooltip} isEnabled={true}>
                    Communication Methods{" "}
                    <Icon icon={faQuestionCircle} className={styles.infoIcon} />
                  </Tooltip>
                </h3>
                <div>
                  <div className={styles.facts}>
                    <div key="Email" className={styles.fact}>
                      <div className={styles.label}>Email</div>
                      <div className={styles.value}>
                        {primaryContactPreferences.primary_contact_email?.email_address}
                      </div>
                      {primaryContactPreferences.primary_contact_email && (
                        <TextChip
                          isInline={true}
                          variant={
                            primaryContactPreferences.primary_contact_email.is_marketing_subscribed
                              ? "info"
                              : "grey"
                          }
                        >
                          {primaryContactPreferences.primary_contact_email.is_marketing_subscribed
                            ? "Subscribed"
                            : "Unsubscribed"}
                        </TextChip>
                      )}
                    </div>
                    <div key="Phone" className={styles.fact}>
                      <div className={styles.label}>Phone</div>
                      <div className={styles.value}>
                        {primaryContactPreferences.primary_contact_number?.number_display}
                      </div>
                      {primaryContactPreferences.primary_contact_number && (
                        <TextChip
                          isInline={true}
                          variant={
                            primaryContactPreferences.primary_contact_number.is_subscribed
                              ? "info"
                              : "grey"
                          }
                        >
                          {primaryContactPreferences.primary_contact_number.is_subscribed
                            ? "Subscribed"
                            : "Unsubscribed"}
                        </TextChip>
                      )}
                    </div>
                  </div>
                  <Tooltip
                    isEnabled={!isAccountOwner}
                    content={commMethodsDisabledTooltip}
                    position="above"
                  >
                    <Option
                      disabled={!isAccountOwner}
                      onClick={() =>
                        setSearch({ form: "updateCommsEmail", visible: ModalStatus.Open })
                      }
                    >
                      <span
                        className={
                          !isAccountOwner ? styles.disabledOptionTitle : styles.optionTitle
                        }
                      >
                        {commsEmailLabel}
                      </span>
                    </Option>
                  </Tooltip>
                  <Tooltip
                    isEnabled={!isAccountOwner}
                    content={commMethodsDisabledTooltip}
                    position="above"
                  >
                    <Option
                      disabled={!isAccountOwner}
                      onClick={() =>
                        setSearch({ form: "updateCommsPhone", visible: ModalStatus.Open })
                      }
                    >
                      <span
                        className={
                          !isAccountOwner ? styles.disabledOptionTitle : styles.optionTitle
                        }
                      >
                        {commsPhoneLabel}
                      </span>
                    </Option>
                  </Tooltip>
                  <Tooltip
                    isEnabled={!isAccountOwner}
                    content={commMethodsDisabledTooltip}
                    position="above"
                  >
                    <Option
                      disabled={!isAccountOwner}
                      onClick={() =>
                        setSearch({
                          form: "updateCommunicationPreferences",
                          visible: ModalStatus.Open,
                        })
                      }
                    >
                      <span
                        className={
                          !isAccountOwner ? styles.disabledOptionTitle : styles.optionTitle
                        }
                      >
                        Update communication preferences
                      </span>
                    </Option>
                  </Tooltip>
                </div>
              </div>
            )}
            <div className={styles.item}>
              <h3 className={styles.heading}>Payment Methods </h3>
              <div className={styles.options}>
                {client.payment_methods.map((method) => (
                  <Option
                    key={method.id}
                    isPrimary={client.primary_payment_method?.id === method.id}
                    autoshipChipContent={
                      method.relevant_subscription_count
                        ? `${method.relevant_subscription_count} ${pluralized(
                            method.relevant_subscription_count,
                            "Autoship",
                          )} use this method`
                        : undefined
                    }
                    onClick={() =>
                      setSearch({
                        form: "updatePayment",
                        visible: ModalStatus.Open,
                        id: `${method.id}`,
                      })
                    }
                  >
                    <PaymentMethod
                      method={method}
                      styles={{
                        label: styles.optionTitle,
                        description: styles.optionDescription,
                      }}
                    />
                  </Option>
                ))}
                <AddOption
                  onClick={() => setSearch({ form: "newPayment", visible: ModalStatus.Open })}
                >
                  Add payment method
                </AddOption>
              </div>
            </div>
            <div className={styles.item}>
              <h3 className={styles.heading}>Addresses</h3>
              <div className={styles.options}>
                {client.addresses.map((address) => {
                  const relevantSusbcriptionCount =
                    address.active_subscription_count + address.paused_subscription_count;
                  return (
                    <Option
                      key={address.id}
                      isPrimary={client.primary_address?.id === address.id}
                      onClick={() =>
                        setSearch({
                          form: "updateAddress",
                          visible: ModalStatus.Open,
                          id: `${address.id}`,
                        })
                      }
                      autoshipChipContent={
                        relevantSusbcriptionCount
                          ? `${relevantSusbcriptionCount} ${pluralized(
                              relevantSusbcriptionCount,
                              "Autoship ships",
                              "Autoships ship",
                            )} here`
                          : undefined
                      }
                    >
                      <Address
                        address={address}
                        styles={{
                          name: styles.optionTitle,
                          address: styles.optionDescription,
                          street: styles.optionDescription,
                        }}
                      />
                    </Option>
                  );
                })}
                <AddOption
                  onClick={() => setSearch({ form: "newAddress", visible: ModalStatus.Open })}
                >
                  Add address
                </AddOption>
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.heading}>
                Information required for controlled substances
                <button onClick={openControlledSubstancesInfoDrawer} type="button">
                  <Icon icon={faCircleQuestion} className={styles.infoIcon} />
                </button>
              </div>

              <div className={styles.options}>
                <Option
                  onClick={() =>
                    setSearch({
                      form: "updateControlledSubstancesFields",
                      visible: ModalStatus.Open,
                    })
                  }
                >
                  <span className={styles.optionTitle}>Update DOB and Driver&apos;s License</span>
                </Option>
              </div>
            </div>
          </div>
        </div>
      </Body>
    </Page>
  );
};

const AccountPage = () => {
  return (
    <Protected>
      <ActiveAccountPage />
    </Protected>
  );
};

export default AccountPage;
